/* Reviews — filter, sort, paginate, lightbox, helpful votes.
 *
 * Everything operates on the already-rendered review DOM (the facts live in
 * data-* attributes), so there is no second copy of the review data on the
 * page and no request needed to filter. Without JS the full list renders —
 * only the controls are progressive enhancement.
 */
(function () {
  'use strict';

  class Reviews extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-review-list]');
      if (!this.list) {
        this.bindFormToggle();
        return;
      }

      this.reviews = Array.from(this.querySelectorAll('[data-review]'));
      this.emptyNote = this.querySelector('[data-review-empty]');
      this.moreBtn = this.querySelector('[data-review-more]');
      this.perPage = parseInt(this.dataset.perPage, 10) || 10;
      this.shown = this.perPage;

      this.filters = { star: null, photos: false, video: false, verified: false };
      this.sort = 'recent';

      this.bindFilters();
      this.bindSort();
      this.bindMore();
      this.bindHelpful();
      this.bindLightbox();
      this.bindFormToggle();

      this.apply();
    }

    /* ------------------------------------------------------ filtering -- */
    bindFilters() {
      this.querySelectorAll('[data-filter]').forEach((chip) => {
        chip.addEventListener('click', () => {
          const key = chip.dataset.filter;
          this.filters[key] = !this.filters[key];
          chip.setAttribute('aria-pressed', this.filters[key] ? 'true' : 'false');
          this.shown = this.perPage;
          this.apply();
        });
      });

      this.querySelectorAll('[data-filter-star]').forEach((row) => {
        row.addEventListener('click', () => {
          const star = parseInt(row.dataset.filterStar, 10);
          this.filters.star = this.filters.star === star ? null : star;
          this.querySelectorAll('[data-filter-star]').forEach((other) => {
            other.setAttribute(
              'aria-pressed',
              parseInt(other.dataset.filterStar, 10) === this.filters.star ? 'true' : 'false'
            );
          });
          this.shown = this.perPage;
          this.apply();
        });
      });

      const reset = this.querySelector('[data-filter-reset]');
      if (reset) {
        reset.addEventListener('click', () => {
          this.filters = { star: null, photos: false, video: false, verified: false };
          this.querySelectorAll('[data-filter]').forEach((c) => c.setAttribute('aria-pressed', 'false'));
          this.querySelectorAll('[data-filter-star]').forEach((c) => c.setAttribute('aria-pressed', 'false'));
          this.shown = this.perPage;
          this.apply();
        });
      }
    }

    bindSort() {
      const select = this.querySelector('[data-review-sort]');
      if (!select) return;
      select.addEventListener('change', () => {
        this.sort = select.value;
        this.apply();
      });
    }

    bindMore() {
      if (!this.moreBtn) return;
      this.moreBtn.addEventListener('click', () => {
        this.shown += this.perPage;
        this.apply();
        // Move focus to the first newly revealed review so keyboard users
        // aren't dumped back at the top of the list.
        const visible = this.visibleReviews();
        const next = visible[this.shown - this.perPage];
        if (next) {
          next.setAttribute('tabindex', '-1');
          next.focus();
        }
      });
    }

    matches(review) {
      const f = this.filters;
      if (f.star && parseInt(review.dataset.rating, 10) !== f.star) return false;
      if (f.photos && parseInt(review.dataset.photos, 10) < 1) return false;
      if (f.video && parseInt(review.dataset.videos, 10) < 1) return false;
      if (f.verified && review.dataset.verified !== 'true') return false;
      return true;
    }

    visibleReviews() {
      const matched = this.reviews.filter((r) => this.matches(r));
      const sort = this.sort;
      matched.sort(function (a, b) {
        if (sort === 'highest') return b.dataset.rating - a.dataset.rating;
        if (sort === 'lowest') return a.dataset.rating - b.dataset.rating;
        if (sort === 'helpful') return b.dataset.helpful - a.dataset.helpful;
        return (parseInt(b.dataset.date, 10) || 0) - (parseInt(a.dataset.date, 10) || 0);
      });
      return matched;
    }

    apply() {
      const matched = this.visibleReviews();

      this.reviews.forEach((r) => {
        r.hidden = true;
      });

      matched.slice(0, this.shown).forEach((r, i) => {
        r.hidden = false;
        r.style.order = i;
      });

      if (this.emptyNote) this.emptyNote.hidden = matched.length !== 0;
      if (this.moreBtn) this.moreBtn.hidden = matched.length <= this.shown;
      this.list.style.display = 'grid';
    }

    /* -------------------------------------------------------- helpful -- */
    /* Stored locally. A real vote count needs a write path Liquid doesn't
       have; rather than fake a server round-trip, the button records the
       shopper's own vote and the published count stays the merchant's. */
    bindHelpful() {
      const key = 'tb-helpful-votes';
      let voted = [];
      try {
        voted = JSON.parse(localStorage.getItem(key) || '[]');
      } catch (e) {
        voted = [];
      }

      this.querySelectorAll('[data-review-helpful]').forEach((btn) => {
        const review = btn.closest('[data-review]');
        const id = review ? review.dataset.index : null;
        if (id && voted.indexOf(id) > -1) btn.setAttribute('aria-pressed', 'true');

        btn.addEventListener('click', () => {
          const pressed = btn.getAttribute('aria-pressed') === 'true';
          btn.setAttribute('aria-pressed', pressed ? 'false' : 'true');
          if (!id) return;
          const next = pressed ? voted.filter((v) => v !== id) : voted.concat(id);
          voted = next;
          try {
            localStorage.setItem(key, JSON.stringify(next));
          } catch (e) {
            /* storage unavailable */
          }
        });
      });
    }

    /* ------------------------------------------------------- lightbox -- */
    bindLightbox() {
      const lightbox = document.querySelector('[data-review-lightbox]');
      if (!lightbox) return;
      const img = lightbox.querySelector('img');

      document.addEventListener('click', function (event) {
        const trigger = event.target.closest('[data-review-media]');
        if (trigger) {
          img.src = trigger.dataset.mediaSrc;
          lightbox.showModal();
          return;
        }
        if (event.target.closest('[data-lightbox-close]') || event.target === lightbox) {
          lightbox.close();
        }
      });
    }

    /* ----------------------------------------------------- form toggle - */
    bindFormToggle() {
      const shell = document.querySelector('[data-review-form-shell]');
      if (!shell) return;

      // If the form posted (success or error), it must already be visible.
      if (shell.querySelector('.form-status')) shell.hidden = false;

      document.querySelectorAll('[data-review-form-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          shell.hidden = !shell.hidden;
          if (!shell.hidden) {
            const first = shell.querySelector('input, textarea, select');
            if (first) first.focus();
            shell.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      });

      const cancel = shell.querySelector('[data-review-form-cancel]');
      if (cancel) {
        cancel.addEventListener('click', function () {
          shell.hidden = true;
          const toggle = document.querySelector('[data-review-form-toggle]');
          if (toggle) toggle.focus();
        });
      }
    }
  }

  if (!customElements.get('tb-reviews')) customElements.define('tb-reviews', Reviews);
})();
